import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AsYouType } from "libphonenumber-js";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { Loader2, ChevronDown, Check } from "lucide-react";
import { toast } from "sonner";
import AuthLayout from "@/pages/auth/AuthLayout";
import { COUNTRIES, DEFAULT_COUNTRY, PLACEHOLDERS } from "@/lib/countries";
import {
  authPhoneCheck,
  authSendCode,
  authVerifyCode,
  authCreateAccount,
  authSetPin,
  authLogin,
  authResetPinRequest,
  authResetPinComplete,
  authCheckReferral,
  setSessionToken,
  getPublicPlatformStatus,
} from "@/lib/afritask";

// ============================================================ Enter Phone
export function EnterPhone() {
  const navigate = useNavigate();
  const [country, setCountry] = useState(DEFAULT_COUNTRY);
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [waitlistMode, setWaitlistMode] = useState(false);
  const inputRef = useRef(null);

  // Focus phone input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Pull public platform status so heading reflects waitlist_mode
  useEffect(() => {
    let cancelled = false;
    getPublicPlatformStatus()
      .then((s) => { if (!cancelled) setWaitlistMode(!!s.waitlist_mode); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Live-format the number using libphonenumber-js AsYouType formatter.
  // AsYouType only formats national numbers reliably when given the international
  // form, so we prefix with the country dial code, format, then strip the prefix
  // back off for display in the local-portion input.
  const formatLocal = (digits, dial) => {
    if (!digits) return "";
    const intl = new AsYouType().input(`+${dial}${digits}`);
    const prefix = `+${dial} `;
    return intl.startsWith(prefix) ? intl.slice(prefix.length) : digits;
  };

  const handleChange = (raw) => {
    // Strip non-digit chars so backspace / paste behaves predictably
    const digits = raw.replace(/\D/g, "");
    setPhone(formatLocal(digits, country.dial));
  };

  const submit = async () => {
    const trimmed = phone.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    try {
      // Combine dial code + local number to give backend an unambiguous E.164 candidate
      const digitsOnly = trimmed.replace(/\D/g, "");
      const full = digitsOnly.startsWith(country.dial)
        ? `+${digitsOnly}`
        : `+${country.dial}${digitsOnly.replace(/^0+/, "")}`;
      const r = await authPhoneCheck(full, country.iso);
      if (r.exists) {
        navigate(`/auth/enter-pin?phone=${encodeURIComponent(r.phone_normalized)}`);
      } else {
        navigate(
          `/auth/verify-code?phone=${encodeURIComponent(r.phone_normalized)}&request_id=${r.request_id}&mode=signup`
        );
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  const placeholder = PLACEHOLDERS[country.iso] || "Phone number";
  const disabled = !phone.trim() || busy;

  return (
    <AuthLayout
      title={waitlistMode ? "Join the waitlist or log in" : "Welcome to AfriTask AI"}
      subtitle={
        waitlistMode
          ? "New signups join the waitlist. Existing accounts log in as normal."
          : "Enter your phone number to continue."
      }
      step="1 of 4"
    >
      <form
        onSubmit={(e) => { e.preventDefault(); submit(); }}
        className="space-y-4"
      >
        <div className="space-y-1.5">
          <Label>Phone number</Label>
          <div className="flex items-stretch overflow-hidden rounded-lg border border-neutral-200 focus-within:ring-2 focus-within:ring-amber-100">
            <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="flex items-center gap-1.5 border-r border-neutral-200 bg-neutral-50 px-3 text-sm font-medium text-neutral-700 transition hover:bg-neutral-100"
                  data-testid="country-picker-trigger"
                  aria-label="Select country"
                >
                  <span className="text-base leading-none">{country.flag}</span>
                  <span className="tabular-nums">+{country.dial}</span>
                  <ChevronDown className="h-3.5 w-3.5 text-neutral-400" />
                </button>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                className="w-72 p-0"
                data-testid="country-picker-popover"
              >
                <Command>
                  <CommandInput placeholder="Search country…" data-testid="country-search-input" />
                  <CommandList>
                    <CommandEmpty>No country found.</CommandEmpty>
                    <CommandGroup>
                      {COUNTRIES.map((c) => (
                        <CommandItem
                          key={c.iso}
                          value={`${c.name} ${c.dial} ${c.iso}`}
                          onSelect={() => {
                            setCountry(c);
                            setPickerOpen(false);
                            // Re-format any existing input under the new country
                            const digits = phone.replace(/\D/g, "");
                            setPhone(formatLocal(digits, c.dial));
                            setTimeout(() => inputRef.current?.focus(), 50);
                          }}
                          data-testid={`country-option-${c.iso}`}
                          className="cursor-pointer"
                        >
                          <span className="mr-2 text-base leading-none">{c.flag}</span>
                          <span className="flex-1 truncate">{c.name}</span>
                          <span className="ml-2 text-xs tabular-nums text-neutral-500">
                            +{c.dial}
                          </span>
                          {c.iso === country.iso && (
                            <Check className="ml-2 h-3.5 w-3.5 text-amber-700" />
                          )}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            <Input
              ref={inputRef}
              type="tel"
              inputMode="tel"
              placeholder={placeholder}
              value={phone}
              onChange={(e) => handleChange(e.target.value)}
              className="border-none focus-visible:ring-0"
              data-testid="phone-input"
            />
          </div>
        </div>
        <Button
          type="submit"
          className="w-full bg-amber-700 hover:bg-amber-800"
          disabled={disabled}
          data-testid="phone-continue-btn"
        >
          {busy ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Sending…
            </>
          ) : (
            "Continue"
          )}
        </Button>
      </form>
    </AuthLayout>
  );
}

// ============================================================ Enter PIN
export function EnterPin() {
  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const phone = params.get("phone") || "";
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (pin.length < 4) return;
    setBusy(true);
    try {
      const r = await authLogin(phone, pin);
      setSessionToken(r.session_token);
      localStorage.setItem("afritask.user_id", r.user_id);
      localStorage.setItem("afritask.username", r.username);
      // Silent transition — dashboard IS the confirmation. No welcome banner.
      if (r.waitlisted) {
        navigate("/waitlist");
      } else if (r.onboarding_step === "profile") {
        navigate("/app/profile-setup");
      } else {
        navigate("/app/dashboard");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Login failed");
      setPin("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title="Enter your PIN" subtitle={`For ${phone}`} step="2 of 2">
      <div className="space-y-4">
        <div className="space-y-1.5">
          <Label>PIN</Label>
          <Input
            type="password"
            inputMode="numeric"
            pattern="\d*"
            maxLength={6}
            placeholder="••••"
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
            onKeyDown={(e) => e.key === "Enter" && submit()}
            className="text-center text-lg tracking-widest"
            data-testid="pin-input"
            autoFocus
          />
        </div>
        <Button
          className="w-full bg-amber-700 hover:bg-amber-800"
          disabled={pin.length < 4 || busy}
          onClick={submit}
          data-testid="pin-login-btn"
        >
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Log in
        </Button>
        <div className="flex items-center justify-between text-xs">
          <button className="text-amber-700 hover:underline" onClick={() => navigate("/auth/reset-pin")} data-testid="forgot-pin-link">
            Forgot PIN?
          </button>
          <button className="text-neutral-500 hover:underline" onClick={() => navigate("/auth/enter-phone")}>
            Use a different number
          </button>
        </div>
      </div>
    </AuthLayout>
  );
}

// ============================================================ Verify Code
export function VerifyCode() {
  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const phone = params.get("phone") || "";
  const [requestId, setRequestId] = useState(params.get("request_id") || "");
  const mode = params.get("mode") || "signup";
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(60);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const resend = async () => {
    setBusy(true);
    try {
      const r = await (mode === "reset" ? authResetPinRequest(phone) : authSendCode(phone, "signup"));
      setRequestId(r.request_id);
      toast.success("New code sent");
      setCooldown(60);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Resend failed");
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    if (code.length !== 6) return;
    setBusy(true);
    try {
      if (mode === "reset") {
        // For reset flow we bounce back to set-pin page with the verification token
        // Reset flow uses a different single endpoint — collect new PIN there.
        navigate(`/auth/set-pin?mode=reset&phone=${encodeURIComponent(phone)}&request_id=${requestId}&code=${code}`);
        return;
      }
      const r = await authVerifyCode(requestId, code, phone);
      toast.success("Verified");
      navigate(`/auth/create-account?vt=${encodeURIComponent(r.verification_token)}&phone=${encodeURIComponent(phone)}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid code");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title="Verify your phone" subtitle={`We sent a 6-digit code to ${phone}`} step={mode === "reset" ? "Reset — Step 2" : "2 of 4"}>
      <div className="space-y-4">
        <div className="space-y-1.5">
          <Label>6-digit code</Label>
          <Input
            inputMode="numeric"
            maxLength={6}
            placeholder="••••••"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            onKeyDown={(e) => e.key === "Enter" && submit()}
            className="text-center text-xl font-mono tracking-widest"
            data-testid="code-input"
            autoFocus
          />
        </div>
        <Button
          className="w-full bg-amber-700 hover:bg-amber-800"
          disabled={code.length !== 6 || busy}
          onClick={submit}
          data-testid="verify-code-btn"
        >
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Verify
        </Button>
        <div className="text-center text-xs">
          {cooldown > 0 ? (
            <span className="text-neutral-500">Resend in {cooldown}s</span>
          ) : (
            <button className="text-amber-700 hover:underline" onClick={resend} data-testid="resend-code-btn">
              Resend code
            </button>
          )}
        </div>
      </div>
    </AuthLayout>
  );
}

// ============================================================ Create Account
export function CreateAccount() {
  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const vt = params.get("vt") || "";
  const phone = params.get("phone") || "";
  const prefillCode = (params.get("ref") || localStorage.getItem("afritask.pending_ref") || "").toUpperCase();
  const [fullName, setFullName] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [refCode, setRefCode] = useState(prefillCode);
  const [refState, setRefState] = useState({ status: "idle", referrer: null, isSystem: false });
  const [busy, setBusy] = useState(false);
  const [showRefField, setShowRefField] = useState(true);

  // Live-validate the referral code (debounced) against the backend.
  useEffect(() => {
    const code = (refCode || "").trim().toUpperCase();
    if (!code) {
      setRefState({ status: "idle", referrer: null, isSystem: false });
      return;
    }
    if (code.length < 6) {
      setRefState({ status: "typing", referrer: null, isSystem: false });
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const r = await authCheckReferral(code);
        if (cancelled) return;
        if (r.valid) {
          setRefState({ status: "valid", referrer: r.referrer_display_name || r.referrer_username, isSystem: r.is_system });
        } else {
          setRefState({ status: "invalid", referrer: null, isSystem: false });
        }
      } catch {
        if (!cancelled) setRefState({ status: "invalid", referrer: null, isSystem: false });
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [refCode]);

  const displayNameOk = /^[A-Za-z0-9][A-Za-z0-9 .,'\-]{0,28}[A-Za-z0-9.']$/.test(displayName.trim()) && displayName.trim().length >= 2;

  const submit = async () => {
    if (!fullName.trim()) return;
    if (!displayNameOk) {
      toast.error("Display name must be 2-30 chars, letters/numbers/spaces only");
      return;
    }
    if (refState.status !== "valid") {
      toast.error(refState.status === "invalid" ? "Referral code not found" : "Referral code required");
      return;
    }
    setBusy(true);
    try {
      const r = await authCreateAccount({
        verification_token: vt,
        full_name: fullName.trim(),
        display_name: displayName.trim(),
        email: email.trim() || undefined,
        referral_code: refCode.trim().toUpperCase(),
      });
      localStorage.removeItem("afritask.pending_ref");
      navigate(`/auth/set-pin?user_id=${r.user_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Signup failed");
    } finally {
      setBusy(false);
    }
  };

  const useSystemCode = () => setRefCode("AFRITASK");

  return (
    <AuthLayout title="Create your account" subtitle={`Registering ${phone}`} step="3 of 4">
      <div className="space-y-4">
        <div className="space-y-1.5">
          <Label>Full name</Label>
          <Input
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            placeholder="e.g. Adaeze Okonkwo"
            data-testid="fullname-input"
            autoFocus
          />
        </div>
        <div className="space-y-1.5">
          <Label>Display name <span className="text-red-500">*</span></Label>
          <Input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="e.g. Adebayo O."
            maxLength={30}
            data-testid="displayname-input"
          />
          <p className="text-xs text-neutral-500">
            What other users see on leaderboards + referrals. 2-30 chars.
          </p>
        </div>
        <div className="space-y-1.5">
          <Label>Email <span className="text-xs text-neutral-400">(optional)</span></Label>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            data-testid="email-input"
          />
        </div>
        <div className="space-y-1.5">
          <Label>
            Referral code <span className="text-red-500">*</span>
          </Label>
          <Input
            value={refCode}
            onChange={(e) => setRefCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 8))}
            placeholder="ABC123"
            className="font-mono uppercase tracking-widest"
            data-testid="referral-code-input"
            maxLength={8}
          />
          {refState.status === "invalid" && (
            <p className="text-xs text-red-500" data-testid="referral-code-error">
              Referral code not found
            </p>
          )}
          {refState.status === "valid" && !refState.isSystem && (
            <p className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800" data-testid="referral-code-valid">
              <Check className="h-3 w-3" />
              Invited by {refState.referrer}
            </p>
          )}
          {refState.status === "valid" && refState.isSystem && (
            <p className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800" data-testid="referral-code-system">
              <Check className="h-3 w-3" />
              Signing up as a founding member
            </p>
          )}
          {refState.status !== "valid" && (
            <p className="text-xs text-neutral-500">
              Ask a friend for their code — you both get bonus points.{" "}
              <button
                type="button"
                onClick={useSystemCode}
                className="font-mono font-semibold text-amber-700 hover:underline"
                data-testid="use-afritask-code-btn"
              >
                Don&apos;t have one? Use AFRITASK
              </button>
            </p>
          )}
        </div>
        <Button
          className="w-full bg-amber-700 hover:bg-amber-800"
          disabled={!fullName.trim() || !displayNameOk || refState.status !== "valid" || busy}
          onClick={submit}
          data-testid="create-account-btn"
        >
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Continue
        </Button>
      </div>
    </AuthLayout>
  );
}

// ============================================================ Set PIN
export function SetPin() {
  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const mode = params.get("mode") || "signup";
  const userId = params.get("user_id") || "";
  const phone = params.get("phone") || "";
  const requestId = params.get("request_id") || "";
  const code = params.get("code") || "";
  const [pin, setPin] = useState("");
  const [pinConfirm, setPinConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const canSubmit = pin.length >= 4 && pin.length <= 6 && (pin.length === 4 || pin.length === 6) && pin === pinConfirm;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      if (mode === "reset") {
        const r = await authResetPinComplete({
          request_id: requestId, code, phone, new_pin: pin, new_pin_confirm: pinConfirm,
        });
        setSessionToken(r.session_token);
        localStorage.setItem("afritask.user_id", r.user_id);
        // Silent transition — dashboard IS the confirmation.
        navigate("/app/dashboard");
      } else {
        const r = await authSetPin({ user_id: userId, pin, pin_confirm: pinConfirm });
        setSessionToken(r.session_token);
        localStorage.setItem("afritask.user_id", r.user_id);
        // Waitlist gate: newly-created accounts under waitlist_mode go straight
        // to the waitlist page instead of profile-setup.
        if (r.waitlisted) {
          navigate("/waitlist");
        } else {
          // Silent transition — profile-setup screen IS the confirmation.
          navigate("/app/profile-setup");
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout
      title={mode === "reset" ? "Set new PIN" : "Set your PIN"}
      subtitle="Choose a 4 or 6 digit PIN. You'll use this to log in from now on."
      step={mode === "reset" ? "Reset — Step 3" : "4 of 4"}
    >
      <div className="space-y-4">
        <div className="space-y-1.5">
          <Label>PIN</Label>
          <Input
            type="password"
            inputMode="numeric"
            maxLength={6}
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
            placeholder="4 or 6 digits"
            className="text-center text-lg tracking-widest"
            data-testid="new-pin-input"
            autoFocus
          />
        </div>
        <div className="space-y-1.5">
          <Label>Confirm PIN</Label>
          <Input
            type="password"
            inputMode="numeric"
            maxLength={6}
            value={pinConfirm}
            onChange={(e) => setPinConfirm(e.target.value.replace(/\D/g, ""))}
            placeholder="Repeat"
            className="text-center text-lg tracking-widest"
            data-testid="new-pin-confirm-input"
          />
        </div>
        {pin && pin.length !== 4 && pin.length !== 6 && (
          <p className="text-xs text-red-500">PIN must be exactly 4 or 6 digits.</p>
        )}
        <Button className="w-full bg-amber-700 hover:bg-amber-800" disabled={!canSubmit || busy} onClick={submit} data-testid="set-pin-submit-btn">
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} {mode === "reset" ? "Reset PIN" : "Set PIN & continue"}
        </Button>
      </div>
    </AuthLayout>
  );
}

// ============================================================ Reset PIN
export function ResetPin() {
  const navigate = useNavigate();
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!phone.trim()) return;
    setBusy(true);
    try {
      const r = await authResetPinRequest(phone);
      navigate(
        `/auth/verify-code?phone=${encodeURIComponent(r.phone_normalized)}&request_id=${r.request_id}&mode=reset`
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title="Reset your PIN" subtitle="Enter the phone linked to your account — we'll send a code by SMS." step="Reset — Step 1">
      <div className="space-y-4">
        <div className="space-y-1.5">
          <Label>Phone number</Label>
          <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="0801 234 5678" data-testid="reset-phone-input" autoFocus />
        </div>
        <Button className="w-full bg-amber-700 hover:bg-amber-800" disabled={!phone.trim() || busy} onClick={submit} data-testid="reset-send-btn">
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Send reset code
        </Button>
      </div>
    </AuthLayout>
  );
}
